import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import type { WorkContextResponse } from "../queries/useWorkContextQuery";

export type GeneralSettingsInput = {
  siteName: string;
  timezone: string;
  sapPlantCode: string | null;
};

async function mutationError(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as {
    error?: unknown;
  } | null;
  return typeof body?.error === "string" ? body.error : fallback;
}

export function useUpdateGeneralSettingsMutation() {
  const queryClient = useQueryClient();
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useMutation({
    mutationFn: async (input: GeneralSettingsInput) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].settings.general.$patch({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
        },
        json: input,
      });
      if (!response.ok)
        throw new Error(
          await mutationError(response, "Unable to update the site settings."),
        );
      return {
        facilityId: selectedFacility.id,
        saved: (await response.json()) as GeneralSettingsInput,
      };
    },
    onSuccess: ({ facilityId, saved }) => {
      // The Work context is read from a replica that may not have the new
      // values yet, so patch the cache directly instead of refetching it.
      queryClient.setQueryData<WorkContextResponse>(
        ["work-context"],
        (current) =>
          current && {
            ...current,
            customers: current.customers.map((customer) => ({
              ...customer,
              facilities: customer.facilities.map((facility) =>
                facility.id === facilityId
                  ? {
                      ...facility,
                      name: saved.siteName,
                      timezone: saved.timezone,
                      sapPlantCode: saved.sapPlantCode,
                    }
                  : facility,
              ),
            })),
          },
      );
      toast.add("Site settings updated.", { icon: true });
    },
    onError: (error: Error) => toast.error(error.message, { icon: true }),
  });
}
